import {
  ActionIcon,
  Anchor,
  Group,
  Stack,
  Switch,
  SwitchProps,
  Text,
  Textarea,
  TextareaProps,
  TextInput,
  Tooltip,
} from '@mantine/core';
import React, { useCallback } from 'react';

import { PortalConfigEmailSettingsType } from '@portals/api/partners';
// import { useConfirmNavigation } from '@portals/framework';
import { ReactComponent as RotateLeft } from '@portals/icons/linear/rotate-left.svg';

import { useBrandingContext } from '../../branding.context';
import { DesignColorInput } from '../customer-portal/tool-panel/tabs/design/fields/ColorField';
import { useDesignField } from '../customer-portal/tool-panel/tabs/design/fields/hooks';
import { DesignImageInput } from '../customer-portal/tool-panel/tabs/design/fields/ImageField';

export function EmailsForm({ disabled = false }: { disabled?: boolean }) {
  const footerLabel = useDesignField(['email_settings', 'markdown']);

  const { updatePortalConfig, defaultValues, portalConfig } =
    useBrandingContext();

  const onChange = useCallback(
    (key: keyof PortalConfigEmailSettingsType, value: string | boolean) => {
      updatePortalConfig((curr) => ({
        ...curr,
        email_settings: {
          ...(portalConfig.email_settings || {}),
          [key]: value,
        },
      }));
    },
    [updatePortalConfig, portalConfig?.email_settings]
  );

  // useConfirmNavigation({
  //   isActive: !isPristine,
  //   confirmationModal: {
  //     title: 'Leave page without saving?',
  //     description:
  //       'You are about to leave the white-label progress and discard all your work. Are you sure?',
  //     confirmationLabel: 'Discard and Leave',
  //     cancelLabel: 'Keep Editing',
  //   },
  //   onConfirm: revertChanges,
  // });

  return (
    <Stack spacing="md" p="md">
      <Text size="md" color="blue_gray.9">
        General
      </Text>

      <Switch
        labelPosition="left"
        label="Use branded sender name"
        styles={switchStyles}
        onClick={(e) => onChange('custom_from_email', e.currentTarget.checked)}
        checked={portalConfig?.email_settings?.custom_from_email}
        disabled={disabled}
      />

      <Text size="md" color="blue_gray.9">
        Branding
      </Text>

      <Stack>
        <DesignColorInput
          label="Header Color"
          valuePath={['email_settings', 'color']}
          disabled={disabled}
        />

        <DesignImageInput
          isRemovable={false}
          disabled={disabled}
          valuePath={['email_settings', 'logo']}
          field={{
            title: 'Logo',
            name: 'logo',
            cropConfig: {
              aspectRatio: 1,
              height: 200,
              width: 200,
            },
            previewConfig: {
              width: 100,
              height: 100,
            },
            height: 200,
            inputProps: {
              mb: 15,
            },
          }}
        />

        <TextInput
          disabled={disabled}
          label="Link associated with logo"
          value={
            portalConfig?.email_settings?.site_url ||
            defaultValues?.email_settings?.site_url
          }
          onChange={(event) => onChange('site_url', event.target.value)}
        />

        <DesignColorInput
          disabled={disabled}
          label="Primary Color"
          valuePath={['email_settings', 'primary']}
        />

        <DesignColorInput
          disabled={disabled}
          label="Secondary Color"
          valuePath={['email_settings', 'secondary']}
        />

        <DesignColorInput
          disabled={disabled}
          label="Tertiary Color"
          valuePath={['email_settings', 'tertiary']}
        />
      </Stack>

      <Text size="md" color="blue_gray.9">
        Footer
      </Text>

      <Stack>
        <Textarea
          disabled={disabled}
          styles={textareaStyles}
          minRows={3}
          value={footerLabel.value}
          onChange={(event) => footerLabel.onChange(event.target.value)}
          description={
            <Group align="center" position="right">
              <Text>
                Supports{' '}
                <Anchor
                  href="https://www.markdownguide.org/basic-syntax/"
                  target="_blank"
                >
                  Markdown
                </Anchor>
              </Text>
            </Group>
          }
          rightSection={
            footerLabel.value === footerLabel.defaultValue ? null : (
              <Tooltip label="Reset to default">
                <ActionIcon
                  onClick={() => footerLabel.onChange(footerLabel.defaultValue)}
                >
                  <RotateLeft width={16} height={16} />
                </ActionIcon>
              </Tooltip>
            )
          }
        />
      </Stack>
    </Stack>
  );
}

const textareaStyles: TextareaProps['styles'] = (theme) => ({
  rightSection: {
    paddingTop: theme.spacing.xs,
    alignItems: 'flex-start',
  },
});

const switchStyles: SwitchProps['styles'] = (theme) => ({
  body: {
    justifyContent: 'space-between',
  },
});
